/**
 * Admin Service
 * Frontend service for admin-specific operations
 */

import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000/api';

export interface SystemStats {
  totalManufacturers: number;
  totalUsers: number;
  totalProducts: number;
  totalTransfers: number;
  totalScans: number;
  pendingManufacturers: number;
  pendingUsers: number;
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: Date;
  }>;
}

export interface ManufacturerDetails {
  id: string;
  name: string;
  email: string;
  status: string;
  createdAt: Date;
  stats: {
    totalUsers: number;
    totalProducts: number;
    totalBatches: number;
  };
}

class AdminService {
  private getAuthHeader() {
    const token = localStorage.getItem('auth_token');
    return {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    };
  }

  /**
   * Get system-wide statistics
   */
  async getSystemStats(): Promise<SystemStats> {
    const response = await axios.get(
      `${API_URL}/admin/stats`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Get all manufacturers
   */
  async getAllManufacturers(): Promise<ManufacturerDetails[]> {
    const response = await axios.get(
      `${API_URL}/admin/manufacturers`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Get pending manufacturers
   */
  async getPendingManufacturers(): Promise<ManufacturerDetails[]> {
    const response = await axios.get(
      `${API_URL}/admin/manufacturers/pending`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Get manufacturer details
   */
  async getManufacturerDetails(id: string): Promise<ManufacturerDetails> {
    const response = await axios.get(
      `${API_URL}/admin/manufacturers/${id}`,
      this.getAuthHeader()
    );
    return response.data;
  }

  /**
   * Approve manufacturer
   */
  async approveManufacturer(id: string): Promise<void> {
    await axios.post(
      `${API_URL}/admin/manufacturers/${id}/approve`,
      {},
      this.getAuthHeader()
    );
  }

  /**
   * Reject manufacturer
   */
  async rejectManufacturer(id: string, reason?: string): Promise<void> {
    await axios.post(
      `${API_URL}/admin/manufacturers/${id}/reject`,
      { reason },
      this.getAuthHeader()
    );
  }

  /**
   * Get detailed manufacturer registration information for admin review
   */
  async getManufacturerRegistrationDetails(id: string): Promise<any> {
    const response = await axios.get(
      `${API_URL}/admin/manufacturers/${id}/registration`,
      this.getAuthHeader()
    );
    return response.data;
  }
}

export const adminService = new AdminService();